import React from 'react';
import clsx from 'clsx';
import { SkeletonCardFields } from 'components/Loading';
import { makeStyles } from '@material-ui/styles';
import {
  Box,
  Button,
  Divider,
  Grid,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Tooltip
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import EventDriven from './components/EventDriven';

import { Card, CardHeader, CardContent, CardActions } from '@material-ui/core';
import { constants } from 'views/Campaign';
import LicenseCheck from 'LicenseCheck';
import { CloseIcon, SelectInput } from 'components';
import { getCampaignAutomationOptions } from 'helpers/templateHelper';

const {
  campaignBasicOccurrenceTypeOptions,
  campaignStandardOccurrenceTypeOptions,
  campaignOnDemandOccurrenceTypeOptions,
  campaignOccurrenceTypeOptions,
  campaignOccurrenceTypes
} = constants;

const useStyles = makeStyles(theme => ({
  root: {},
  content: {
    padding: theme.spacing(3)
  },
  infoIcon: {
    marginBottom: '0.5rem'
  },
  infoIcon2: {
    marginBottom: '4px',
    marginLeft: '1rem'
  },
  type: {
    display: 'flex',
    alignItems: 'center'
  },
  iconLabel: {
    marginLeft: '0.7rem'
  },
  radioGroup: {
    marginLeft: '1rem'
  },
  switchGroup: {
    display: 'flex'
  },
  switch: {
    marginLeft: '-0.7rem'
  },
  headerContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between'
  }
}));

const Settings = props => {
  const {
    className,
    staticContext,
    campaign,
    next,
    back,
    templates,
    showNav,
    ...rest
  } = props;
  const {
    thisCampaign,
    goToNextStep,
    onBlur,
    onFocus,
    isLoading,
    handleChange,
    shouldShowError,
    getErrorMessage,
    isItineraryOnly,
    isCampaignReadOnly,
    closeCampaign
  } = campaign;
  const classes = useStyles();
  const licenses = LicenseCheck();
  const { hasFeatureStandard, hasFeatureOnDemand, hasFeatureSurvey } = licenses;

  const isNewCampaign = !thisCampaign.uuid;

  const canChangeType = () =>
    !isNewCampaign || thisCampaign.hasMovedToNextStep || !!thisCampaign._isCopy;

  const canChangeAutomationType = () =>
    !!thisCampaign._isCopy ||
    !!thisCampaign.scheduleRule ||
    !!thisCampaign.communicationTemplateUuid ||
    thisCampaign.templates?.length > 0 ||
    thisCampaign.attachmentTemplateUuids.length > 0;

  const isBackPortingEnabled = () => {
    const isCampaignOccurrenceType =
      thisCampaign.campaignOccurrenceType === 'SCHEDULED';

    return (
      (isNewCampaign && isCampaignOccurrenceType) ||
      (thisCampaign.backport && isNewCampaign) // allows user to turn off if they have switched CampaignOccurrenceType
    );
  };

  const isRecurrenceEnabled = () => {
    const isCampaignOccurrenceType =
      thisCampaign.campaignOccurrenceType === 'ON_DEMAND';

    return (
      (isNewCampaign && isCampaignOccurrenceType) ||
      (thisCampaign?.recurrence && isNewCampaign) // allows user to turn off if they have switched CampaignOccurrenceType
    );
  };

  const handleBackPortingChange = () => {
    handleChange({
      target: {
        value: !thisCampaign.backport,
        name: 'backport'
      }
    });
  };
  const handleRecurrenceChange = () => {
    handleChange({
      target: {
        value: !thisCampaign.recurrence,
        name: 'recurrence'
      }
    });
  };

  const handlePromotionalChange = () => {
    handleChange({
      target: {
        value: !thisCampaign.promotional,
        name: 'promotional'
      }
    });
  };

  const SkeletonCardContent = props => {
    return (
      <CardContent>
        <Grid container spacing={0}>
          <SkeletonCardFields numberOfRows={4} />
        </Grid>
      </CardContent>
    );
  };

  return (
    <div className={classes.root}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form autoComplete="off">
          <div className={classes.headerContainer}>
            <CardHeader
              subheader=""
              title="Campaign Settings"
              titleTypographyProps={{ variant: 'h1' }}
            />
            {(next || back || showNav) && (
              <CloseIcon onClickClose={closeCampaign} />
            )}
          </div>
          <Divider />
          {isLoading && <SkeletonCardContent />}
          {!isLoading && (
            <CardContent>
              <Grid item xs={12}>
                <Grid item md={6} xs={12} className={classes.inputGrid}>
                  <TextField
                    fullWidth
                    label="Name your campaign"
                    margin="dense"
                    name="name"
                    onChange={handleChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    required
                    value={thisCampaign.name || ''}
                    variant="outlined"
                    error={shouldShowError('name')}
                    helperText={getErrorMessage('name')}
                    disabled={isCampaignReadOnly()}
                  />
                </Grid>
              </Grid>
              <Grid item md={6} xs={12}>
                <SelectInput
                  fullWidth
                  label="Automation"
                  margin="dense"
                  name="campaignOccurrenceType"
                  onChange={handleChange}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  disabled={canChangeAutomationType()}
                  value={thisCampaign.campaignOccurrenceType || ''}
                  error={shouldShowError('campaignOccurrenceType')}
                  helperText={getErrorMessage('campaignOccurrenceType') || ''}
                  options={getCampaignAutomationOptions({
                    campaignBasicOccurrenceTypeOptions,
                    campaignOccurrenceTypeOptions,
                    campaignOnDemandOccurrenceTypeOptions,
                    campaignStandardOccurrenceTypeOptions,
                    isItineraryOnly: isItineraryOnly(),
                    hasFeatureSurvey: hasFeatureSurvey(),
                    hasFeatureOnDemand: hasFeatureOnDemand(),
                    hasFeatureStandard: hasFeatureStandard()
                  })}
                />
              </Grid>
              <EventDriven
                campaign={campaign}
                disabled={canChangeAutomationType()}
              />
              <Grid item xs={12} className={classes.type}>
                <Grid item>
                  <FormControl
                    component="fieldset"
                    error={shouldShowError('type')}>
                    <FormControlLabel
                      label="Campaign Type:"
                      labelPlacement="start"
                      className={classes.iconLabel}
                      control={
                        <RadioGroup
                          aria-label="campaign type"
                          name="type"
                          value={thisCampaign.type}
                          onChange={handleChange}
                          row
                          className={classes.radioGroup}>
                          <FormControlLabel
                            value="EMAIL"
                            label="Email"
                            disabled={canChangeType()}
                            labelPlacement="end"
                            control={<Radio color="primary" />}
                          />
                          <FormControlLabel
                            value="SMS"
                            label="SMS"
                            disabled={canChangeType() || thisCampaign.campaignOccurrenceType === 'EXTERNAL_SURVEY_RESPONSE'}
                            labelPlacement="end"
                            control={<Radio color="primary" />}
                          />
                        </RadioGroup>
                      }
                    />
                    <FormHelperText>{getErrorMessage('type')}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item>
                  <Tooltip
                    className={classes.infoIcon2}
                    title="Only Email type is applicable for External survey response">
                    <InfoIcon />
                  </Tooltip>
                </Grid>
              </Grid>
              <Grid item xs={12} className={classes.switchGroup}>
                {thisCampaign.campaignOccurrenceType === campaignOccurrenceTypes.SCHEDULED && (
                  <Grid item md={6} xs={12}>
                    <FormControlLabel
                      className={classes.iconLabel}
                      control={
                        <Switch
                          className={classes.switch}
                          checked={!!thisCampaign.backport}
                          name="backport"
                          onChange={handleBackPortingChange}
                          onFocus={onFocus}
                          onBlur={onBlur}
                          disabled={!isBackPortingEnabled()}
                          color="primary"
                          inputProps={{ 'aria-label': 'primary checkbox' }}
                        />
                      }
                      label="Apply to Existing Reservations"
                    />{' '}
                    <Tooltip
                      className={classes.infoIcon}
                      title="This option only applies to scheduled campaigns. It can only be toggled when creating a new campaign">
                      <InfoIcon />
                    </Tooltip>
                    <FormHelperText error>
                      {getErrorMessage('backport')}
                    </FormHelperText>
                  </Grid>
                )}
                {thisCampaign.campaignOccurrenceType === 'ON_DEMAND' && (
                  <Grid item md={6} xs={12}>
                    <FormControlLabel
                      className={classes.iconLabel}
                      control={
                        <Switch
                          className={classes.switch}
                          checked={!!thisCampaign.recurrence}
                          name="recurrence"
                          onChange={handleRecurrenceChange}
                          onFocus={onFocus}
                          onBlur={onBlur}
                          disabled={!isRecurrenceEnabled()}
                          color="primary"
                          inputProps={{ 'aria-label': 'primary checkbox' }}
                        />
                      }
                      label="Recurring Campaign"
                    />{' '}
                    <Tooltip
                      className={classes.infoIcon}
                      title="This option only applies to on demand campaigns. It can only be toggled when creating a new campaign">
                      <InfoIcon />
                    </Tooltip>
                    <FormHelperText error>
                      {getErrorMessage('backport')}
                    </FormHelperText>
                  </Grid>
                )}
              </Grid>
              <Grid item xs={12} className={classes.switchGroup}>
                {(thisCampaign.campaignOccurrenceType !== campaignOccurrenceTypes.INTERNAL_SURVEY_RESPONSE ) && (
                  <Grid item md={6} xs={12}>
                    <FormControlLabel
                      className={classes.iconLabel}
                      control={
                        <Switch
                          className={classes.switch}
                          checked={!!thisCampaign.promotional}
                          name="promotional"
                          onChange={handlePromotionalChange}
                          onFocus={onFocus}
                          onBlur={onBlur}
                          disabled={isCampaignReadOnly()}
                          color="primary"
                          inputProps={{ 'aria-label': 'primary checkbox' }}
                        />
                      }
                      label="Promotional Campaign"
                    />{' '}
                    <Tooltip
                      className={classes.infoIcon}
                      title="This option is not applicable to internal survey response campaigns.">
                      <InfoIcon />
                    </Tooltip>
                    <FormHelperText error>
                      {getErrorMessage('backport')}
                    </FormHelperText>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          )}
          {(next || back || showNav) && (
            <>
              <Divider />
              <CardActions className={classes.cardActions}>
                <Box flexGrow={1}>&nbsp;</Box>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={goToNextStep}>
                  Next
                </Button>
              </CardActions>
            </>
          )}
        </form>
      </Card>
    </div>
  );
};

export default Settings;
